import * as React from "react";
import { useToasts } from "react-toast-notifications";
import { Dialog } from "../../bits/Dialog/Dialog";
import { TextArea } from "../../bits/TextArea/TextArea";
import store from "../../../../store";
import { OfficeRequestStatus, SignatureData } from "intake_shared/src/types";
import moment from "moment";
import { DateFormats } from "intake_shared/src/types";
import styles from "./RejectRequestDialog.module.scss";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";

interface RejectRequestDialogProps {
    rejectRequest: boolean;
    officeRequestToReject: {
        clinician_name: string;
        start_date: Date;
        end_date: Date;
        location: string;
        office: string;
        id: string;
        requester: string;
        category: string;
    };
    setRejectRequest: (value: boolean) => void;
    setRefreshKey: (value: React.SetStateAction<number>) => void;
    requestToRejectId: string;
    loggedInUserEmail: string;
    viewFrom: string;
}

export function RejectRequestDialog({
    rejectRequest,
    officeRequestToReject,
    setRejectRequest,
    setRefreshKey,
    requestToRejectId,
    loggedInUserEmail,
    viewFrom,
}: RejectRequestDialogProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [rejectionReason, setRejectionReason] = React.useState("");
    const [loading, setLoading] = React.useState(false);

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            addToast("Please provide a reason for rejection", {
                appearance: "error",
            });
            return;
        }

        setLoading(true);
        try {
            await store.rejectClinicianOfficeWindow({
                office_request_to_reject_details: {
                    ...officeRequestToReject,
                    rejection_reason: rejectionReason,
                    office_window_request_id: requestToRejectId,
                    status: OfficeRequestStatus.REJECTED,
                    office_manager: loggedInUserEmail,
                    category: officeRequestToReject.category,
                },
                type:
                    viewFrom === "delete_office_window_request"
                        ? "delete_office_window_request"
                        : "",
                intakeSignature: intakeSignature || ({} as SignatureData),
            });

            addToast("Request rejected successfully", {
                appearance: "success",
            });
            setRejectRequest(false);
            setRejectionReason("");
            setRefreshKey((prevKey) => prevKey + 1);
        } catch (error) {
            console.error("Error rejecting request:", error);
            addToast("An error occurred while rejecting the request", {
                appearance: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setRejectRequest(false);
        setRejectionReason("");
    };

    const getTitle = () => {
        return `${officeRequestToReject.clinician_name} - ${
            officeRequestToReject.location
        } (${officeRequestToReject.office}) ${moment(
            officeRequestToReject.start_date
        ).format(DateFormats.DAY)} - ${moment(
            officeRequestToReject.start_date
        ).format(DateFormats.HOUR_MIN_FORMAT)} - ${moment(
            officeRequestToReject.end_date
        ).format(DateFormats.HOUR_MIN_FORMAT)}`;
    };

    const getDescription = () => {
        if (viewFrom === "delete_office_window_request") {
            return "Provide a reason for rejection.";
        }
        return "Are you sure you want to delete this window? This will delete the office window from the clinicians office window and delete all availabilities set under it.";
    };

    return (
        <Dialog
            open={rejectRequest}
            onClose={handleClose}
            title={getTitle()}
            isDeleting
            isCancelBtnDisabled={loading}
            isSubmitBtnDisabled={rejectionReason === "" || loading}
            saveText={loading ? "Rejecting..." : "Reject Request"}
            onSave={handleReject}
            onCancel={handleClose}
        >
            <div className={styles.container}>
                <p className={styles.note}>{getDescription()}</p>

                <TextArea
                    label="Reason"
                    placeholder="Please provide a reason..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    isRequired
                    className={styles.reason}
                />
            </div>
        </Dialog>
    );
}
